import { BINARY_SIGNED_URL_EXTENSION } from '@bariendo/lib/extensions';
import { getReferenceString, MedplumClient } from '@medplum/core';
import { Attachment } from '@medplum/fhirtypes';
import { alert } from './alert';
import { fetchBot } from './services';

export function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
}

export async function uploadAttachments(medplum: MedplumClient, files: File[]): Promise<Attachment[]> {
  const attachments: Attachment[] = [];

  try {
    for (const file of files) {
      const binary = await medplum.createBinary({
        data: file,
        contentType: file.type,
      });

      attachments.push({
        id: binary.id,
        url: getReferenceString(binary),
        contentType: binary.contentType,
        size: file.size,
        title: file.name,
        extension: [
          {
            // This extension is used to send the signed binary urls to Twilio since
            // It's not possible to generate it just from the Binary resource.
            // It should be removed before storing the attachment in a resource.
            url: BINARY_SIGNED_URL_EXTENSION,
            valueUrl: binary.url,
          },
        ],
      });
    }

    return attachments;
  } catch (err) {
    await Promise.all(attachments.map((attachment) => medplum.deleteResource('Binary', attachment.id as string)));
    throw new Error("We couldn't upload all attachments.", { cause: err });
  }
}

export async function downloadAttachment(medplum: MedplumClient, attachment: Attachment): Promise<void> {
  if (!attachment.url) return;

  try {
    const response = await fetchBot<{
      success: boolean;
      contentType?: string;
      data?: string;
      size?: number;
      error?: string;
    }>(medplum, 'download-binary', { binaryUrl: attachment.url });

    if (response?.success && response.data) {
      const binaryData = atob(response.data);
      const bytes = new Uint8Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i++) {
        bytes[i] = binaryData.charCodeAt(i);
      }

      const blob = new Blob([bytes], { type: response.contentType || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);

      const filename = attachment.title || `download-${Date.now()}${getFileExtension(response.contentType || '')}`;

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } else {
      throw new Error(response?.error || 'No data received');
    }
  } catch {
    alert.error({ title: "Couldn't download", message: 'Please try again or contact support.' });
  }
}

function getFileExtension(contentType: string): string {
  const mimeToExt: Record<string, string> = {
    'image/jpeg': '.jpeg',
    'image/jpg': '.jpg',
    'image/png': '.png',
    'image/gif': '.gif',
    'application/pdf': '.pdf',
  };
  return mimeToExt[contentType] || '';
}

export async function getAttachmentBlob(medplum: MedplumClient, attachment: Attachment) {
  if (!attachment.url) return;

  const response = await fetchBot<{
    success: boolean;
    contentType?: string;
    data?: string;
    size?: number;
    error?: string;
  }>(medplum, 'download-binary', { binaryUrl: attachment.url });

  if (!response?.success || !response.data) {
    throw new Error(response?.error || 'No data received');
  }

  const binaryData = atob(response.data!);
  const bytes = new Uint8Array(binaryData.length);
  for (let i = 0; i < binaryData.length; i++) {
    bytes[i] = binaryData.charCodeAt(i);
  }

  const contentType = response.contentType || 'application/octet-stream';

  const blob = new Blob([bytes], { type: contentType });
  const url = URL.createObjectURL(blob);

  const filename = attachment.title || `download-${Date.now()}${getFileExtension(response.contentType || '')}`;

  return {
    url,
    filename,
    size: response.size!,
    contentType: contentType,
  };
}

export function downloadAttachmentBlob(url: string, filename: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}
